import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import api from "../lib/api";

const initial = {
  full_name: "", age: "", email: "", phone: "", city: "",
  mode: "Online", reason: "", preferred_date: "", preferred_time: "",
};

const TIMES = ["09:00", "10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00"];

export default function Booking() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.preferred_date || !form.preferred_time) {
      toast.error("Please fill the required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", { ...form, age: parseInt(form.age || "0", 10) });
      setDone(true);
      setForm(initial);
      toast.success("Request received! Check your email for confirmation.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <section className="container-soft py-24 text-center max-w-2xl" data-testid="booking-thankyou">
        <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: "var(--accent-lavender)" }}>
          <CheckCircle2 size={28} style={{ color: "var(--primary-deep)" }}/>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mb-4" style={{ color: "var(--text-primary)" }}>Thank you for reaching out.</h1>
        <p className="mb-8" style={{ color: "var(--text-secondary)" }}>
          Your consultation request has been received. I'll get back to you within 24-48 hours via email or phone.
          A confirmation email is on its way.
        </p>
        <button onClick={() => setDone(false)} className="rounded-full px-6 py-3 text-sm" style={{ background: "var(--primary)", color: "#fff" }} data-testid="booking-new-btn">
          Submit another request
        </button>
      </section>
    );
  }

  return (
    <div data-testid="booking-page">
      <section className="container-soft pt-16 md:pt-24 pb-10 max-w-3xl">
        <div className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: "var(--primary-deep)" }}>Booking</div>
        <h1 className="font-display text-5xl sm:text-6xl mb-6" style={{ color: "var(--text-primary)" }}>Book a Consultation</h1>
        <p className="text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          Share a few details below and I'll get back to you within 24-48 hours to confirm your session.
        </p>
      </section>

      <section className="container-soft pb-24 max-w-3xl">
        <form onSubmit={submit} className="rounded-3xl p-8 md:p-10 space-y-5" style={{ background: "var(--surface)", border: "1px solid var(--border-soft)" }}>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Full Name *" testId="b-name">
              <input required value={form.full_name} onChange={set("full_name")} className="form-input" data-testid="booking-full-name"/>
            </Field>
            <Field label="Age">
              <input type="number" min="10" max="100" value={form.age} onChange={set("age")} className="form-input" data-testid="booking-age"/>
            </Field>
            <Field label="Email *">
              <input type="email" required value={form.email} onChange={set("email")} className="form-input" data-testid="booking-email"/>
            </Field>
            <Field label="Phone Number">
              <input value={form.phone} onChange={set("phone")} className="form-input" data-testid="booking-phone"/>
            </Field>
            <Field label="City">
              <input value={form.city} onChange={set("city")} className="form-input" data-testid="booking-city"/>
            </Field>
            <Field label="Session Mode">
              <input value="Online (audio / video call)" readOnly className="form-input" style={{ background: "var(--bg-secondary)", color: "var(--text-secondary)" }} data-testid="booking-mode"/>
            </Field>
            <Field label="Preferred Date *">
              <input type="date" required value={form.preferred_date} onChange={set("preferred_date")} className="form-input" data-testid="booking-date"/>
            </Field>
            <Field label="Preferred Time *">
              <select required value={form.preferred_time} onChange={set("preferred_time")} className="form-input" data-testid="booking-time">
                <option value="">Select a time</option>
                {TIMES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Reason for Consultation">
            <textarea rows={4} value={form.reason} onChange={set("reason")} className="form-input resize-none" placeholder="Briefly describe what you'd like support with (optional)" data-testid="booking-reason"/>
          </Field>

          <button type="submit" disabled={loading} className="rounded-full px-7 py-3.5 text-sm w-full md:w-auto disabled:opacity-60" style={{ background: "var(--primary)", color: "#fff" }} data-testid="booking-submit">
            {loading ? "Sending..." : "Submit Request"}
          </button>
        </form>
      </section>

      <style>{`
        .form-input {
          width: 100%; padding: 12px 14px; border-radius: 14px;
          border: 1px solid var(--border-soft);
          background: var(--bg-primary); color: var(--text-primary);
          font-size: 15px; transition: border-color 0.2s, box-shadow 0.2s;
        }
        .form-input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(143,163,150,0.18); outline: none; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm mb-2" style={{ color: "var(--text-secondary)" }}>{label}</span>
      {children}
    </label>
  );
}
